import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ArrowLeft, Calendar, CheckCircle, Copy, Loader2, User } from 'lucide-react';
import { apiClient } from '../utils/api';
import { ShopSelectionGate } from '../components/ShopSelectionGate';
import { useShopScopedPage } from '../hooks/useShopScopedReload';
import { DateInput } from '../components/DateInput';
import { localizeAppointmentApiMessage } from '../utils/localizeApiMessage';

type AppointmentType = 'CONSULTATION' | 'TATTOO_SESSION' | 'PIERCING';

interface SelectOption {
  id: string;
  name: string;
  price?: number;
  estimatedDuration?: number;
  category?: string;
}

interface CreatedAnonymousClient {
  id: string;
  firstName?: string;
  anonymousCode?: string;
}

interface AppointmentResult {
  created: boolean;
  id?: string;
  error?: string;
}

interface ShopSlot {
  start: string;
  end: string;
  isAvailable: boolean;
  practitionerId?: string;
}

const appointmentTypeToServiceCategory = (type: AppointmentType): string => {
  if (type === 'TATTOO_SESSION') return 'TATTOO';
  if (type === 'PIERCING') return 'PIERCING';
  return 'CONSULTATION';
};

const appointmentTypeLabel = (type: AppointmentType): string => {
  if (type === 'TATTOO_SESSION') return 'Tatouage';
  if (type === 'PIERCING') return 'Piercing';
  return 'Rendez-vous';
};

const addMinutesToDateTimeLocal = (dateTimeLocal: string, minutes: number): string => {
  if (!dateTimeLocal) return '';
  const date = new Date(dateTimeLocal);
  if (Number.isNaN(date.getTime())) return '';
  date.setMinutes(date.getMinutes() + Math.max(1, Math.round(minutes)));
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const mins = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${mins}`;
};

const formatLocalDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const extractSlotLocalTime = (slotStart: string): string => {
  const timePart = slotStart.includes('T') ? slotStart.split('T')[1] : slotStart;
  return timePart.slice(0, 5);
};

const toDateTimeLocal = (date: string, time: string): string => `${date}T${time}`;

const dateTimeLocalToIso = (value: string): string => {
  const normalized = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value) ? `${value}:00` : value;
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return value;
  return date.toISOString();
};

const filterPastSlotsForToday = (slots: ShopSlot[], selectedDate: string): ShopSlot[] => {
  if (selectedDate !== formatLocalDate(new Date())) return slots;
  const now = Date.now();
  return slots.filter((slot) => {
    const start = new Date(slot.start);
    if (!Number.isNaN(start.getTime())) return start.getTime() > now;
    const [hours, minutes] = extractSlotLocalTime(slot.start).split(':').map(Number);
    const local = new Date();
    local.setHours(hours || 0, minutes || 0, 0, 0);
    return local.getTime() > now;
  });
};

const pickBookableSlots = (slots: ShopSlot[], requiredSlots: number): ShopSlot[] => {
  const sorted = [...slots].sort((left, right) => {
    if ((left.practitionerId || '') !== (right.practitionerId || '')) {
      return (left.practitionerId || '').localeCompare(right.practitionerId || '');
    }
    return left.start.localeCompare(right.start);
  });

  const bookable: ShopSlot[] = [];
  for (const slot of sorted) {
    if (!slot.isAvailable) continue;
    if (requiredSlots <= 1) {
      bookable.push(slot);
      continue;
    }

    let consecutive = 1;
    let currentEnd = slot.end;
    for (let index = 1; index < requiredSlots; index += 1) {
      const next = sorted.find((candidate) => (
        candidate.isAvailable
        && (candidate.practitionerId || '') === (slot.practitionerId || '')
        && Math.abs(new Date(candidate.start).getTime() - new Date(currentEnd).getTime()) < 1000
      ));
      if (!next) break;
      consecutive += 1;
      currentEnd = next.end;
    }
    if (consecutive >= requiredSlots) bookable.push(slot);
  }

  const unique = new Map<string, ShopSlot>();
  for (const slot of bookable) {
    const key = extractSlotLocalTime(slot.start);
    if (!unique.has(key)) unique.set(key, slot);
  }
  return Array.from(unique.values()).sort((left, right) => (
    extractSlotLocalTime(left.start).localeCompare(extractSlotLocalTime(right.start))
  ));
};

export function AnonymousClientCreatePage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { needsShopSelection, canLoad, currentShopId, currentShop } = useShopScopedPage({ requireSingleShop: true });

  const [firstName, setFirstName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [created, setCreated] = useState<CreatedAnonymousClient | null>(null);
  const [copied, setCopied] = useState(false);
  const [appointmentResult, setAppointmentResult] = useState<AppointmentResult | null>(null);

  const [appointmentType, setAppointmentType] = useState<AppointmentType>('PIERCING');
  const [serviceId, setServiceId] = useState('');
  const [practitionerId, setPractitionerId] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [availableSlots, setAvailableSlots] = useState<ShopSlot[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [slotsError, setSlotsError] = useState<string | null>(null);

  const shopId = currentShopId || '';
  const [services, setServices] = useState<SelectOption[]>([]);
  const [practitioners, setPractitioners] = useState<SelectOption[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [optionsError, setOptionsError] = useState<string | null>(null);

  const filteredServices = useMemo(() => {
    const category = appointmentTypeToServiceCategory(appointmentType);
    return services.filter((service) => service.category === category);
  }, [services, appointmentType]);

  const selectedService = useMemo(
    () => services.find((service) => service.id === serviceId),
    [services, serviceId],
  );

  useEffect(() => {
    if (!shopId) {
      setServices([]);
      return;
    }

    const fetchServices = async () => {
      try {
        setOptionsLoading(true);
        setOptionsError(null);
        const response = await apiClient(`/api/admin/services?limit=1000&shopId=${encodeURIComponent(shopId)}`);
        const data = await response.json();
        if (!data?.success) {
          setServices([]);
          setOptionsError(t('anonymousCreate.appointment.loadServicesError'));
          return;
        }
        setServices(
          (data.data?.services || []).map((service: { id: string; name: string; basePrice?: number; estimatedDuration?: number; category?: string }) => ({
            id: service.id,
            name: service.name,
            price: Number(service.basePrice || 0),
            estimatedDuration: Number(service.estimatedDuration || 60),
            category: service.category,
          })),
        );
      } catch {
        setServices([]);
        setOptionsError(t('anonymousCreate.appointment.loadServicesError'));
      } finally {
        setOptionsLoading(false);
      }
    };

    void fetchServices();
  }, [shopId, t]);

  useEffect(() => {
    const compatible = filteredServices;
    if (serviceId && compatible.some((service) => service.id === serviceId)) return;
    setServiceId(compatible.length === 1 ? compatible[0].id : '');
    setPractitionerId('');
  }, [appointmentType, services, filteredServices, serviceId]);

  useEffect(() => {
    if (!shopId || !serviceId) {
      setPractitioners([]);
      return;
    }

    const fetchPractitioners = async () => {
      try {
        const [shopResponse, serviceResponse] = await Promise.all([
          apiClient(`/api/admin/shops/${encodeURIComponent(shopId)}/practitioners`),
          apiClient(`/api/admin/services/${encodeURIComponent(serviceId)}/practitioners`),
        ]);
        const [shopData, serviceData] = await Promise.all([shopResponse.json(), serviceResponse.json()]);
        if (!shopData?.success) {
          setPractitioners([]);
          return;
        }
        const shopPractitioners: SelectOption[] = (shopData.data?.practitioners || [])
          .filter((practitioner: { isActive?: boolean }) => practitioner.isActive)
          .map((practitioner: { practitionerId: string; practitionerFirstName?: string; practitionerLastName?: string }) => ({
            id: practitioner.practitionerId,
            name: `${practitioner.practitionerFirstName || ''} ${practitioner.practitionerLastName || ''}`.trim(),
          }));

        if (!serviceData?.success) {
          setPractitioners(shopPractitioners);
          return;
        }

        const allowedIds = new Set(
          (serviceData.data?.practitioners || [])
            .filter((practitioner: { isOffered?: boolean; isAvailable?: boolean }) => practitioner.isOffered && practitioner.isAvailable)
            .map((practitioner: { id: string }) => practitioner.id),
        );
        const compatible = shopPractitioners.filter((practitioner) => allowedIds.has(practitioner.id));
        setPractitioners(compatible);
        setPractitionerId((prev) => (prev && compatible.some((practitioner) => practitioner.id === prev) ? prev : ''));
      } catch {
        setPractitioners([]);
      }
    };

    void fetchPractitioners();
  }, [shopId, serviceId]);

  useEffect(() => {
    if (!shopId || !selectedDate || !serviceId) {
      setAvailableSlots([]);
      setLoadingSlots(false);
      setSlotsError(null);
      return;
    }

    let cancelled = false;

    const fetchSlots = async () => {
      try {
        setLoadingSlots(true);
        setSlotsError(null);
        const from = new Date(`${selectedDate}T00:00:00`).toISOString();
        const to = new Date(`${selectedDate}T23:59:59`).toISOString();
        const params = new URLSearchParams({
          shopId,
          from,
          to,
          serviceId,
          bookingSource: 'BACKOFFICE',
        });
        if (practitionerId) params.set('practitionerId', practitionerId);

        const response = await apiClient(`/api/planning/slots?${params.toString()}`);
        const data = await response.json();
        if (cancelled) return;
        if (!data?.success) {
          setAvailableSlots([]);
          setSlotsError(t('anonymousCreate.appointment.loadSlotsError'));
          return;
        }

        const slotSizeMinutes = Math.max(1, Number(data.data?.slotSizeMinutes || 15));
        const durationMinutes = Math.max(1, Number(selectedService?.estimatedDuration || 60));
        const requiredSlots = Math.max(1, Math.ceil(durationMinutes / slotSizeMinutes));
        const nextSlots = filterPastSlotsForToday(
          pickBookableSlots(
            (data.data?.slots || []).map((slot: ShopSlot) => ({
              start: slot.start,
              end: slot.end,
              isAvailable: Boolean(slot.isAvailable),
              practitionerId: slot.practitionerId,
            })),
            requiredSlots,
          ),
          selectedDate,
        );
        setAvailableSlots(nextSlots);
        setScheduledAt((previous) => {
          if (!previous) return previous;
          const stillAvailable = nextSlots.some((slot) => (
            toDateTimeLocal(selectedDate, extractSlotLocalTime(slot.start)) === previous
          ));
          return stillAvailable ? previous : '';
        });
      } catch {
        if (!cancelled) {
          setAvailableSlots([]);
          setSlotsError(t('anonymousCreate.appointment.loadSlotsError'));
        }
      } finally {
        if (!cancelled) setLoadingSlots(false);
      }
    };

    void fetchSlots();
    return () => {
      cancelled = true;
    };
  }, [shopId, selectedDate, serviceId, practitionerId, selectedService?.estimatedDuration, t]);

  const selectedSlotTime = scheduledAt.includes('T') ? scheduledAt.split('T')[1]?.slice(0, 5) : '';

  const validateAppointment = (): boolean => {
    const nextErrors: Record<string, string> = {};
    if (!shopId) nextErrors.shopId = t('anonymousCreate.appointment.shopRequired');
    if (!serviceId) nextErrors.serviceId = t('anonymousCreate.appointment.serviceRequired');
    if (!selectedDate) {
      nextErrors.scheduledAt = t('anonymousCreate.appointment.dateRequired');
    } else if (!scheduledAt) {
      nextErrors.scheduledAt = t('anonymousCreate.appointment.slotRequired');
    } else {
      const appointmentDate = new Date(scheduledAt);
      if (Number.isNaN(appointmentDate.getTime()) || appointmentDate.getTime() < Date.now()) {
        nextErrors.scheduledAt = t('anonymousCreate.appointment.datetimeFuture');
      }
    }
    setFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const createAppointmentForClient = async (clientId: string): Promise<AppointmentResult> => {
    const service = services.find((candidate) => candidate.id === serviceId);
    if (!service) {
      return { created: false, error: t('anonymousCreate.appointment.serviceMissing') };
    }

    const durationMinutes = Math.max(1, Number(service.estimatedDuration || 60));
    const basePrice = Number(service.price || 0);
    const estimatedEndTime = addMinutesToDateTimeLocal(scheduledAt, durationMinutes);

    try {
      const response = await apiClient('/api/admin/appointments', {
        method: 'POST',
        body: JSON.stringify({
          clientId,
          practitionerId: practitionerId || null,
          shopId,
          serviceId,
          scheduledAt: dateTimeLocalToIso(scheduledAt),
          estimatedEndTime: dateTimeLocalToIso(estimatedEndTime),
          status: 'SCHEDULED',
          type: appointmentType,
          notes: '',
          pricing: {
            basePrice,
            deposit: Math.round(basePrice * 0.5 * 100) / 100,
            total: basePrice,
            paymentMethod: 'card',
            currency: 'EUR',
          },
          preparation: {
            beforeAppointment: [],
            whatToBring: [],
            restrictions: [],
          },
          reminders: {
            enabled: false,
            methods: [],
            daysBefore: [],
            customMessage: '',
          },
        }),
      });
      const data = await response.json();
      if (!data?.success) {
        return {
          created: false,
          error:
            localizeAppointmentApiMessage(data?.message)
            || t('anonymousCreate.appointment.createFailed'),
        };
      }
      return {
        created: true,
        id: data?.data?.id || data?.data?.appointment?.id,
      };
    } catch {
      return { created: false, error: t('anonymousCreate.appointment.createFailed') };
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (loading) return;
    if (!validateAppointment()) return;

    setLoading(true);
    setError(null);
    setAppointmentResult(null);

    try {
      const preferredLanguage = i18n.language?.startsWith('en') ? 'en' : 'fr';
      const response = await apiClient('/api/clients/create-anonymous', {
        method: 'POST',
        body: JSON.stringify({
          firstName: firstName.trim() || undefined,
          preferredLanguage,
        }),
      });
      const data = await response.json();

      if (!response.ok || !data.success || !data.data?.client?.id) {
        throw new Error(data.message || t('anonymousCreate.error'));
      }

      const client = data.data.client as CreatedAnonymousClient;
      const nextAppointment = await createAppointmentForClient(client.id);
      setAppointmentResult(nextAppointment);

      setCreated({
        id: client.id,
        firstName: client.firstName || firstName.trim(),
        anonymousCode: client.anonymousCode,
      });
      if (!nextAppointment.created && nextAppointment.error) {
        setError(nextAppointment.error);
      }
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : t('anonymousCreate.error'));
    } finally {
      setLoading(false);
    }
  };

  const copyCode = async () => {
    const code = created?.anonymousCode?.trim();
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setError(t('anonymousCreate.copyFailed'));
    }
  };

  if (needsShopSelection || !canLoad) {
    return (
      <div className="p-6">
        <ShopSelectionGate requireSingleShop />
      </div>
    );
  }

  if (created) {
    const displayName = (created.firstName || '').trim();
    return (
      <div className="p-6">
        <div className="mx-auto max-w-xl">
          <div className="rounded-2xl bg-white p-8 text-center shadow-xl">
            <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-green-100">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
            <h1 className="mb-2 text-2xl font-display font-bold text-textPrimary">
              {t('anonymousCreate.successTitle')}
            </h1>
            <p className="mb-6 text-textSecondary">
              {t('anonymousCreate.successMessage')}
            </p>

            {displayName ? (
              <p className="mb-4 text-lg font-medium text-textPrimary">{displayName}</p>
            ) : (
              <p className="mb-4 text-lg font-medium text-textPrimary">
                {t('anonymousCreate.anonymousLabel')}
              </p>
            )}

            {created.anonymousCode ? (
              <div className="mb-6 rounded-xl border border-purple-300 bg-white px-6 py-5">
                <p className="mb-2 text-sm font-medium text-purple-800">
                  {t('anonymousCreate.codeLabel')}
                </p>
                <p className="text-2xl font-mono font-bold tracking-[0.2em] text-gray-900">
                  {created.anonymousCode}
                </p>
                <button
                  type="button"
                  onClick={copyCode}
                  className="mt-4 inline-flex items-center gap-2 rounded-lg bg-purple-700 px-4 py-2 text-white hover:bg-purple-800"
                >
                  <Copy className="h-4 w-4" />
                  <span>{copied ? t('anonymousCreate.codeCopied') : t('anonymousCreate.copyCode')}</span>
                </button>
              </div>
            ) : null}

            {appointmentResult?.created ? (
              <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 p-4 text-left">
                <p className="font-semibold text-blue-900">{t('anonymousCreate.appointment.createdTitle')}</p>
                <p className="mt-1 text-sm text-blue-800">
                  {appointmentTypeLabel(appointmentType)} {t('anonymousCreate.appointment.scheduledOn')}{' '}
                  {new Date(scheduledAt).toLocaleString('fr-FR')}
                </p>
              </div>
            ) : null}

            {error ? (
              <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                {error}
              </div>
            ) : null}

            <div className="flex flex-col justify-center gap-3 sm:flex-row">
              {appointmentResult?.created && appointmentResult.id ? (
                <button
                  type="button"
                  onClick={() => navigate(`/appointments/${appointmentResult.id}/edit`)}
                  className="btn-primary"
                >
                  {t('anonymousCreate.appointment.view')}
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => navigate(`/clients/${created.id}/edit`)}
                className={appointmentResult?.created ? 'rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50' : 'btn-primary'}
              >
                {t('anonymousCreate.editClient')}
              </button>
              <button
                type="button"
                onClick={() => navigate('/planning/calendar')}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                {t('anonymousCreate.backToList')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mx-auto max-w-3xl">
        <div className="mb-8 text-center">
          <button
            type="button"
            onClick={() => navigate('/planning/calendar')}
            className="mb-4 inline-flex items-center space-x-2 rounded-lg border border-gray-300 px-4 py-2 text-gray-600 shadow-sm transition-colors hover:bg-gray-100 hover:text-gray-800"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>{t('anonymousCreate.backToList')}</span>
          </button>
          <h1 className="mb-2 text-3xl font-display font-bold text-textPrimary">
            {t('anonymousCreate.title')}
          </h1>
          <p className="text-textSecondary">{t('anonymousCreate.subtitle')}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 rounded-2xl bg-white p-8 shadow-xl">
          {error ? (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          ) : null}

          <div>
            <label className="mb-2 flex items-center gap-2 text-sm font-medium text-textPrimary">
              <User className="h-4 w-4" />
              {t('anonymousCreate.firstName')}
            </label>
            <input
              type="text"
              value={firstName}
              onChange={(event) => setFirstName(event.target.value)}
              onBlur={() => setFirstName((value) => value.trim())}
              className="input-field"
              placeholder={t('anonymousCreate.firstNamePlaceholder')}
              disabled={loading}
            />
            <p className="mt-1 text-sm text-gray-500">{t('anonymousCreate.firstNameHint')}</p>
          </div>

          <div className="space-y-5 rounded-xl border border-blue-200 bg-blue-50 p-5">
            <div className="flex items-center font-semibold text-blue-900">
              <Calendar className="mr-2 h-5 w-5" />
              {t('anonymousCreate.appointment.sectionTitle')}
            </div>

            {optionsError ? (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {optionsError}
              </div>
            ) : null}
            {optionsLoading ? (
              <p className="text-sm text-blue-700">{t('anonymousCreate.appointment.loading')}</p>
            ) : null}

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                  {currentShop?.name ? (
                    <div className="md:col-span-2 text-sm text-blue-800">
                      {t('anonymousCreate.appointment.shop')}: <span className="font-medium">{currentShop.name}</span>
                    </div>
                  ) : null}

                  <div>
                    <label className="mb-2 block text-sm font-medium text-textPrimary">
                      {t('anonymousCreate.appointment.type')} *
                    </label>
                    <select
                      value={appointmentType}
                      onChange={(event) => {
                        setAppointmentType(event.target.value as AppointmentType);
                        setServiceId('');
                        setPractitionerId('');
                        setScheduledAt('');
                      }}
                      className="input-field"
                      disabled={loading}
                    >
                      <option value="PIERCING">{t('anonymousCreate.appointment.types.piercing')}</option>
                      <option value="CONSULTATION">{t('anonymousCreate.appointment.types.consultation')}</option>
                      <option value="TATTOO_SESSION">{t('anonymousCreate.appointment.types.tattoo')}</option>
                    </select>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-textPrimary">
                      {t('anonymousCreate.appointment.service')} *
                    </label>
                    <select
                      value={serviceId}
                      onChange={(event) => {
                        setServiceId(event.target.value);
                        setScheduledAt('');
                      }}
                      className={`input-field ${fieldErrors.serviceId ? 'border-red-300' : ''}`}
                      disabled={loading || !shopId}
                    >
                      <option value="">
                        {filteredServices.length === 0
                          ? t('anonymousCreate.appointment.noService')
                          : t('anonymousCreate.appointment.selectService')}
                      </option>
                      {filteredServices.map((service) => (
                        <option key={service.id} value={service.id}>
                          {service.name}
                        </option>
                      ))}
                    </select>
                    {fieldErrors.serviceId ? <p className="mt-1 text-sm text-red-600">{fieldErrors.serviceId}</p> : null}
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-textPrimary">
                      {t('anonymousCreate.appointment.practitioner')}
                    </label>
                    <select
                      value={practitionerId}
                      onChange={(event) => {
                        setPractitionerId(event.target.value);
                        setScheduledAt('');
                      }}
                      className="input-field"
                      disabled={loading || !shopId || !serviceId}
                    >
                      <option value="">
                        {!serviceId
                          ? t('anonymousCreate.appointment.selectServiceFirst')
                          : t('anonymousCreate.appointment.unassigned')}
                      </option>
                      {practitioners.map((practitioner) => (
                        <option key={practitioner.id} value={practitioner.id}>
                          {practitioner.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-textPrimary">
                      {t('anonymousCreate.appointment.date')} *
                    </label>
                    <DateInput
                      value={selectedDate}
                      min={formatLocalDate(new Date())}
                      onChange={(value) => {
                        setSelectedDate(value);
                        setScheduledAt('');
                        setFieldErrors((previous) => {
                          const next = { ...previous };
                          delete next.scheduledAt;
                          return next;
                        });
                      }}
                      disabled={loading}
                      className={`input-field ${fieldErrors.scheduledAt && !selectedDate ? 'border-red-300' : ''}`}
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="mb-2 block text-sm font-medium text-textPrimary">
                      {t('anonymousCreate.appointment.slots')} *
                    </label>
                    {!selectedDate || !serviceId ? (
                      <p className="text-sm text-blue-800">{t('anonymousCreate.appointment.selectDateAndService')}</p>
                    ) : loadingSlots ? (
                      <div className="flex items-center py-4 text-sm text-blue-800">
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t('anonymousCreate.appointment.loadingSlots')}
                      </div>
                    ) : slotsError ? (
                      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                        {slotsError}
                      </div>
                    ) : availableSlots.length === 0 ? (
                      <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-800">
                        <div className="mb-1 flex items-center font-medium">
                          <AlertTriangle className="mr-2 h-4 w-4" />
                          {t('anonymousCreate.appointment.noSlots')}
                        </div>
                        <p>{t('anonymousCreate.appointment.noSlotsHint')}</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-3 gap-2 md:grid-cols-4 lg:grid-cols-6">
                        {availableSlots.map((slot) => {
                          const time = extractSlotLocalTime(slot.start);
                          const isSelected = selectedSlotTime === time;
                          return (
                            <button
                              key={slot.start}
                              type="button"
                              onClick={() => {
                                setScheduledAt(toDateTimeLocal(selectedDate, time));
                                setFieldErrors((previous) => {
                                  const next = { ...previous };
                                  delete next.scheduledAt;
                                  return next;
                                });
                              }}
                              disabled={loading}
                              className={`rounded-lg border-2 px-3 py-2 text-sm transition-colors ${
                                isSelected
                                  ? 'border-primary-500 bg-primary-500 text-white'
                                  : 'border-gray-200 bg-white text-textPrimary hover:border-primary-300'
                              }`}
                            >
                              {time}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {fieldErrors.scheduledAt ? <p className="mt-1 text-sm text-red-600">{fieldErrors.scheduledAt}</p> : null}
                  </div>

                  {selectedService ? (
                    <div className="rounded-lg border border-blue-200 bg-white p-4 text-sm">
                      <p className="mb-2 font-medium text-textPrimary">{t('anonymousCreate.appointment.summary')}</p>
                      <p className="text-gray-600">
                        {t('anonymousCreate.appointment.duration')}: {selectedService.estimatedDuration || 60} min
                      </p>
                      <p className="text-gray-600">
                        {t('anonymousCreate.appointment.price')}: {selectedService.price || 0}€
                      </p>
                    </div>
                  ) : null}
                </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={loading}
              className="btn-primary inline-flex items-center gap-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>{t('anonymousCreate.creating')}</span>
                </>
              ) : (
                t('anonymousCreate.submit')
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
